import { Collab, DeliverableLink, activeLinks, activeUnits } from '@foodfluence/shared';

export type InfluencerCollabPhase = 'draft' | 'draftReview' | 'post' | 'postReview' | 'complete';

const DRAFT_STATUSES = ['pendingDraftSubmission', 'needsInfluencerEdit'];

export function getInfluencerCollabPhase(
  deliverableLinks: Record<string, DeliverableLink[]>
): InfluencerCollabPhase {
  // Voided slots are excluded, or one of them pins the phase at its status forever.
  const all = activeLinks(deliverableLinks);
  if (all.length === 0) return 'complete';
  if (all.some(l => DRAFT_STATUSES.includes(l.status))) return 'draft';
  if (all.some(l => l.status === 'pendingDraftReview'))  return 'draftReview';
  if (all.some(l => l.status === 'pendingSubmission'))   return 'post';
  if (all.some(l => l.status === 'pendingVerification')) return 'postReview';
  return 'complete';
}

// ─── UGC phase helpers ────────────────────────────────────────────────────────

export type UGCCollabPhase = 'content' | 'contentReview' | 'post' | 'postReview' | 'complete';

export function getUGCCollabPhase(collab: Collab): UGCCollabPhase {
  const units = activeUnits(collab.content);

  if (units.some(u => u.status === 'pendingSubmission' || u.contentRejected)) return 'content';
  if (units.some(u => u.status === 'pendingVerification')) return 'contentReview';

  const allLinks = activeLinks(collab.deliverableLinks);
  if (allLinks.some(l => l.status === 'pendingSubmission')) return 'post';
  if (allLinks.some(l => l.status === 'pendingVerification')) return 'postReview';

  return 'complete';
}

// ─── Non-UGC phase helpers ────────────────────────────────────────────────────

/**
 * A link needs influencer edits when it is explicitly flagged for edits (the
 * draft-stage status), or when a rejected post link has been sent back for
 * resubmission. The backend drops rejectionReason as a post is resubmitted
 * (DeliverableRepository.submitPostLink / submitPostFile), so a reason still
 * sitting on a pendingSubmission link means the latest post was rejected.
 *
 * Deliberately ignores `link.feedback`: no code path ever clears it, so a
 * draft that was revised and then approved keeps its old feedback forever and
 * would read as "needs edits" for the rest of the collab.
 */
export function linkNeedsEdits(link: DeliverableLink): boolean {
  return link.status === 'needsInfluencerEdit'
    || (link.status === 'pendingSubmission' && Boolean(link.rejectionReason));
}
